// Espace ressources : fichiers (object storage) et liens partagés, avec permissions par rôle
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Link2, FileText, Trash2, Download, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLES, CATEGORIES_RESSOURCE } from "@/lib/constants";
import { formatJour } from "@/lib/dates";

const peutPublier = (u) => ["admin", "media", "ca"].includes(u?.role) || !!u?.pole_responsable;

const formatTaille = (o) => (!o ? "" : o > 1048576 ? `${(o / 1048576).toFixed(1)} Mo` : `${Math.round(o / 1024)} ko`);

export default function Resources() {
  const { user } = useAuth();
  const [ressources, setRessources] = useState([]);
  const [dialogOuvert, setDialogOuvert] = useState(false);
  const [filtre, setFiltre] = useState("toutes");
  const [envoi, setEnvoi] = useState(false);
  const [form, setForm] = useState({ titre: "", description: "", categorie: "autre", lien: "", roles: [] });
  const [fichier, setFichier] = useState(null);

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get("/resources");
      setRessources(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const publier = async (e) => {
    e.preventDefault();
    if (!fichier && !form.lien.trim()) {
      toast.error("Ajoute un fichier ou un lien.");
      return;
    }
    setEnvoi(true);
    try {
      const fd = new FormData();
      fd.append("titre", form.titre);
      fd.append("description", form.description);
      fd.append("categorie", form.categorie);
      fd.append("roles_autorises", form.roles.join(","));
      fd.append("lien", form.lien);
      if (fichier) fd.append("fichier", fichier);
      await api.post("/resources", fd);
      toast.success("Ressource ajoutée");
      setForm({ titre: "", description: "", categorie: "autre", lien: "", roles: [] });
      setFichier(null);
      setDialogOuvert(false);
      charger();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setEnvoi(false);
    }
  };

  const supprimer = async (id) => {
    if (!window.confirm("Retirer cette ressource ?")) return;
    try {
      await api.delete(`/resources/${id}`);
      toast.success("Ressource retirée");
      charger();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const visibles = filtre === "toutes" ? ressources : ressources.filter((r) => r.categorie === filtre);

  return (
    <div className="space-y-6" data-testid="resources-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl font-bold">Ressources</h1>
          <p className="text-muted-foreground text-sm mt-1">Charte, logos, modèles et documents partagés de l'ASBL</p>
        </div>
        {peutPublier(user) && (
          <Button onClick={() => setDialogOuvert(true)} className="rounded-full gap-1.5" data-testid="new-resource-button">
            <Plus className="h-4 w-4" /> Ajouter une ressource
          </Button>
        )}
      </div>

      <Select value={filtre} onValueChange={setFiltre}>
        <SelectTrigger className="rounded-full w-[220px]" data-testid="resource-filter-categorie"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="toutes">Toutes les catégories</SelectItem>
          {Object.entries(CATEGORIES_RESSOURCE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
        </SelectContent>
      </Select>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="resources-list">
        {visibles.map((r) => (
          <div key={r.id} className="bg-card rounded-2xl border border-border p-5 carte-douce apparition flex flex-col" data-testid={`resource-card-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
              {r.type === "lien" ? <Link2 className="h-6 w-6 text-accent" /> : <FileText className="h-6 w-6 text-primary" />}
              <Badge variant="outline" className="text-[10px]">{CATEGORIES_RESSOURCE[r.categorie] || r.categorie}</Badge>
            </div>
            <h3 className="font-serif text-xl font-semibold mt-3">{r.titre}</h3>
            {r.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-3">{r.description}</p>}
            <p className="text-xs text-muted-foreground/70 mt-2">
              {r.cree_par?.name} · {formatJour(r.created_at)}
              {r.taille ? ` · ${formatTaille(r.taille)}` : ""}
            </p>
            {r.roles_autorises?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {r.roles_autorises.map((ro) => <Badge key={ro} className="text-[10px] bg-secondary text-secondary-foreground border-0">{ROLES[ro] || ro}</Badge>)}
              </div>
            )}
            <div className="flex items-center gap-1 mt-4 pt-3 border-t border-border/60">
              {r.type === "lien" ? (
                <a href={r.lien} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline inline-flex items-center gap-1.5" data-testid={`resource-open-${r.id}`}>
                  <Link2 className="h-3.5 w-3.5" /> Ouvrir le lien
                </a>
              ) : (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/resources/${r.id}/download`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline inline-flex items-center gap-1.5"
                  data-testid={`resource-download-${r.id}`}
                >
                  <Download className="h-3.5 w-3.5" /> Télécharger
                </a>
              )}
              {(user.role === "admin" || r.cree_par?.id === user.id) && (
                <button onClick={() => supprimer(r.id)} className="ml-auto p-2 rounded-full text-muted-foreground hover:text-destructive hover:bg-muted transition-colors" data-testid={`resource-delete-${r.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
        {visibles.length === 0 && (
          <p className="text-sm text-muted-foreground col-span-full text-center py-12" data-testid="resources-empty">
            Aucune ressource pour l'instant.
          </p>
        )}
      </div>

      <Dialog open={dialogOuvert} onOpenChange={setDialogOuvert}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="resource-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Ajouter une ressource</DialogTitle>
            <DialogDescription>Un fichier (15 Mo max) ou un lien externe, visible selon les rôles choisis.</DialogDescription>
          </DialogHeader>
          <form onSubmit={publier} className="space-y-4">
            <div>
              <Label>Titre *</Label>
              <Input required value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} className="rounded-xl mt-1.5" data-testid="resource-titre-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1.5" data-testid="resource-description-input" />
            </div>
            <div>
              <Label>Catégorie</Label>
              <Select value={form.categorie} onValueChange={(v) => setForm({ ...form, categorie: v })}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="resource-categorie-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORIES_RESSOURCE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fichier</Label>
              <label className="mt-1.5 flex items-center gap-3 rounded-xl border border-dashed border-border px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors">
                <Upload className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground truncate">{fichier ? fichier.name : "Choisir un fichier (15 Mo max)"}</span>
                <input type="file" className="hidden" onChange={(e) => setFichier(e.target.files?.[0] || null)} data-testid="resource-file-input" />
              </label>
            </div>
            <div>
              <Label>… ou un lien externe</Label>
              <Input value={form.lien} onChange={(e) => setForm({ ...form, lien: e.target.value })} placeholder="https://drive.google.com/…" className="rounded-xl mt-1.5" data-testid="resource-lien-input" />
            </div>
            <div>
              <Label>Visible par (laisser vide = tout le monde)</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {Object.entries(ROLES).map(([k, v]) => (
                  <label key={k} className="flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-xs cursor-pointer hover:bg-muted/50 transition-colors">
                    <Checkbox
                      checked={form.roles.includes(k)}
                      onCheckedChange={() => setForm({ ...form, roles: form.roles.includes(k) ? form.roles.filter((x) => x !== k) : [...form.roles, k] })}
                      data-testid={`resource-role-${k}`}
                    />
                    {v}
                  </label>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={envoi} className="rounded-full" data-testid="resource-submit-button">
                {envoi ? "Envoi…" : "Publier"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
